import type { ReactElement } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import "app/components/React/UserSettingDropdown/UserSettingsDropdown.scss";

import { useDispatch } from "react-redux";

import Sentry from "@similarweb/sentry-logger";

import { setIsOnboardingWizardOpened } from "app/pages/sales-intelligence/sub-modules/common/store/action-creators";
import {
    USER_CLOSED_ONBOARDING_HUB,
    USER_COMPLETED_ONBOARDING,
    USER_HAS_ONBOARDING_TASKS,
} from "app/pages/sales-intelligence/sub-modules/onboarding/constants/preferences";
import useSidebarHubTasksQuery from "app/pages/sales-intelligence/sub-modules/onboarding/query/useSidebarHubTasksQuery";
import OnboardingHubSidebarContainer from "app/pages/sales-intelligence/sub-modules/onboarding/sidebar-hub/components/OnboardingHubSidebarContainer/OnboardingHubSidebarContainer";
import OnboardingHubContentContainer from "app/pages/sales-intelligence/sub-modules/onboarding/sidebar-hub/containers/OnboardingHubContentContainer";
import useHandleActionByTask from "app/pages/sales-intelligence/sub-modules/onboarding/sidebar-hub/hooks/useHandleActionByTask";
import onboardingHubTrackingService from "app/pages/sales-intelligence/sub-modules/onboarding/sidebar-hub/services/tracking/trackingService";
import { OnboardingGoalQuestionType } from "app/pages/sales-intelligence/sub-modules/onboarding/types/enums";
import type { SidebarHubTaskItem } from "app/pages/sales-intelligence/sub-modules/onboarding/types/SidebarHub";
import { SidebarHubTaskStatus } from "app/pages/sales-intelligence/sub-modules/onboarding/types/SidebarHub";
import { PreferencesService } from "app/services/preferences/preferencesService";

import OnboardingHubDropdown from "../components/OnboardingHubDropdown/OnboardingHubDropdown";

type OnboardingHubPreferences = { userClosedOnboardingHub?: boolean; userHasOnboardingTasks?: boolean };

interface Props {
    isShowHub: boolean;
    onToggleHub: (isOpen: boolean) => void;
    onClose?: () => void;
    onClick: () => void;
}

const OnboardingHubContainer = ({ onClose, isShowHub, onClick, onToggleHub }: Props): ReactElement | null => {
    const isOnboardingCompleted = PreferencesService.get(USER_COMPLETED_ONBOARDING);
    const isUserHasOnboardingTasks = PreferencesService.get(USER_HAS_ONBOARDING_TASKS);

    const dispatch = useDispatch();
    const dropdownRef = useRef<{ closePopup: () => void; state?: unknown }>(null);
    const [isHubClosed, setIsHubClosed] = useState(false);
    const [expandedType, setExpandedType] = useState<OnboardingGoalQuestionType | null>(
        OnboardingGoalQuestionType.DownloadChromeExtension,
    );

    const handleTaskState = (
        isExpand: boolean,
        taskType: OnboardingGoalQuestionType,
        isInSidebar = false,
        taskNumber?: number,
    ): void => {
        if (isInSidebar) {
            onboardingHubTrackingService.trackExpandCollapseTask(isExpand, `sidebar-${taskType}`, "Sidebar Navigation");
            onboardingHubTrackingService.trackClickOnTask(`sidebar-${taskType}`, taskNumber, "Sidebar Navigation");
        } else {
            onboardingHubTrackingService.trackExpandCollapseTask(isExpand, taskType);
        }
        setExpandedType(isExpand ? taskType : null);
    };

    const { isLoading, data, isError, isStale, refetch } = useSidebarHubTasksQuery();

    const handleTask = useHandleActionByTask();

    const refetchData = () => {
        if (!isStale) {
            return;
        }

        refetch();
    };

    useEffect(() => {
        if (!isShowHub) {
            return;
        }

        refetchData();
    }, [isShowHub]);

    useEffect(() => {
        if (isLoading || !data || !data.onboardingTasks?.length || isUserHasOnboardingTasks) {
            return;
        }

        PreferencesService.add<OnboardingHubPreferences>({
            [USER_HAS_ONBOARDING_TASKS]: true,
        });
    }, [data, isLoading]);

    const [completedItemsCount, totalItemsCount] = useMemo(() => {
        if (!data || !data.onboardingTasks?.length) {
            return isError ? [0, 1] : [0, 0];
        }

        return [
            data.onboardingTasks.filter(({ status }) => status === SidebarHubTaskStatus.Completed).length,
            data.onboardingTasks.length,
        ];
    }, [data, isError]);

    const handleButtonHover = () => {
        onboardingHubTrackingService.trackHoverOverHubButton(completedItemsCount, totalItemsCount);

        refetchData();
    };

    const handleButtonClick = () => {
        onboardingHubTrackingService.trackClickHubButton(completedItemsCount, totalItemsCount);

        onClick();

        if (isOnboardingCompleted) {
            return;
        }

        dispatch(setIsOnboardingWizardOpened(true));
    };

    const handleHubClose = () => {
        PreferencesService.add<OnboardingHubPreferences>({
            [USER_CLOSED_ONBOARDING_HUB]: true,
        })
            .then(() => {
                setIsHubClosed(true);
            })
            .catch((err) => {
                Sentry.error("Preferences service: error adding preference (userClosedOnboardingHub)", err);
            });
    };

    const handleToggleHub = (isOpen: boolean) => {
        onToggleHub(isOpen);

        if (!isOpen) {
            return;
        }

        onboardingHubTrackingService.trackOpenHubWindow(completedItemsCount, totalItemsCount);
    };

    const handleDropdownClose = () => {
        dropdownRef.current?.closePopup();
        onClose?.();
    };

    const handleTaskAction = (task: SidebarHubTaskItem): void => {
        onboardingHubTrackingService.trackClickOnTask(task.taskType);

        handleTask(task);

        handleDropdownClose();
    };

    const renderButton = (isActive?: boolean): ReactElement => (
        <OnboardingHubSidebarContainer
            isLoading={isLoading}
            isActive={isActive}
            completedItemsCount={completedItemsCount}
            totalItemsCount={totalItemsCount}
            onClick={!isActive ? handleButtonClick : undefined}
            isShowHubButton={isShowHub}
            isHubClosed={isHubClosed}
            onHovered={handleButtonHover}
            isShowClose={isOnboardingCompleted ? completedItemsCount >= totalItemsCount : null}
            onClose={handleHubClose}
        >
            <OnboardingHubContentContainer
                key="OnboardingHubContent"
                inSidebar={true}
                completedItemsCount={completedItemsCount}
                totalItemsCount={totalItemsCount}
                tasks={data?.onboardingTasks ?? []}
                onTaskActionClick={handleTaskAction}
                onCloseHub={handleDropdownClose}
                handleTaskState={handleTaskState}
                expandedType={expandedType}
                isHubActive={isActive}
            />
        </OnboardingHubSidebarContainer>
    );

    if (isOnboardingCompleted && (!data || !data.onboardingTasks?.length) && !isUserHasOnboardingTasks) {
        return null;
    }

    if (!isOnboardingCompleted) {
        return renderButton();
    }

    return (
        <OnboardingHubDropdown
            dropdownRef={dropdownRef}
            isLoading={isLoading}
            onToggleHub={handleToggleHub}
            renderButton={renderButton}
            content={[
                <OnboardingHubContentContainer
                    key="OnboardingHubContent"
                    inSidebar={false}
                    completedItemsCount={completedItemsCount}
                    totalItemsCount={totalItemsCount}
                    tasks={data?.onboardingTasks ?? []}
                    onTaskActionClick={handleTaskAction}
                    onCloseHub={handleDropdownClose}
                    handleTaskState={handleTaskState}
                    expandedType={expandedType}
                />,
            ]}
        />
    );
};

export default OnboardingHubContainer;
